const STORAGE_KEY = "zg.lowBandwidth";

const SEARCH = globalThis.location?.search ?? "";

type Listener = (lowBandwidth: boolean) => void;
const listeners = new Set<Listener>();

function safeRead(): boolean | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === null ? null : raw === "1";
  } catch {
    return null;
  }
}

/**
 * Guess whether this device wants fewer effects.
 *
 * A 2G/3G connection or a phone reporting very few cores is exactly the
 * audience this event is for, so default them into the lighter mode rather
 * than making them find a toggle after the venue already feels bad.
 */
function detectDefault(): boolean {
  const connection = (
    navigator as Navigator & {
      connection?: { effectiveType?: string; saveData?: boolean };
    }
  ).connection;

  if (connection?.saveData) return true;
  if (connection?.effectiveType && /^(slow-)?2g$/.test(connection.effectiveType)) return true;
  if (typeof navigator.hardwareConcurrency === "number" && navigator.hardwareConcurrency <= 2) {
    return true;
  }
  return false;
}

/**
 * `?lb=0` / `?lb=1` force the quality mode for one page load.
 *
 * The auto-detect below reads the network the page happened to load on, and a
 * phone that reports `2g` for a few seconds enters the lighter venue and stays
 * there. When somebody says the venue "looks basic", this is the first thing to
 * rule out, and the only way to rule it out on a phone is to be able to force
 * it from the address bar. It is deliberately not persisted: it answers a
 * question, it does not change the setting.
 */
const OVERRIDE: boolean | null = (() => {
  const raw = new URLSearchParams(SEARCH).get("lb");
  return raw === "1" ? true : raw === "0" ? false : null;
})();

let lowBandwidth = OVERRIDE ?? safeRead() ?? detectDefault();

/**
 * Why the venue chose the quality it did, in one line, for the on-screen
 * diagnostics. Every input, not just the answer — the answer alone cannot tell
 * you whether it came from a stored preference or from the network guess.
 */
export function describeQuality(): string {
  const connection = (
    navigator as Navigator & {
      connection?: { effectiveType?: string; saveData?: boolean };
    }
  ).connection;
  const stored = safeRead();
  return (
    `low-bandwidth: ${lowBandwidth ? "ON (venue is running reduced effects)" : "off (full effects)"}` +
    ` · override=${OVERRIDE === null ? "none" : String(OVERRIDE)}` +
    ` · stored=${stored === null ? "unset" : String(stored)}` +
    ` · autodetect=${detectDefault()}` +
    ` · effectiveType=${connection?.effectiveType ?? "unknown"}` +
    ` · saveData=${connection?.saveData ?? "unknown"}` +
    ` · cores=${navigator.hardwareConcurrency ?? "unknown"}`
  );
}

export function isLowBandwidth(): boolean {
  return lowBandwidth;
}

export function setLowBandwidth(value: boolean): void {
  if (value === lowBandwidth) return;
  lowBandwidth = value;
  try {
    window.localStorage.setItem(STORAGE_KEY, value ? "1" : "0");
  } catch {
    /* preference is a convenience, not a requirement */
  }
  for (const listener of listeners) listener(value);
}

export function onLowBandwidthChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
